import React, { useEffect, useMemo, useState } from 'react'
import './ServiceScene.css'

/**
 * Motion themed to each service page (keyed by the page slug):
 * - SceneBackdrop: animated layer behind a section (stars, radar, network map, broadcast rings, code, learning icons)
 * - SceneOverlay: effect on the banner photo (dish signal, camera view, rack lights, LIVE label)
 * - SceneWidget: floating card beside the photo that acts out the service (activation, alert, status, …)
 * All of it is decorative and shows a still state for visitors who ask for reduced motion.
 */

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Steps through `count` states every `ms` (stays on the last one for reduced motion). */
function useCycle(count, ms) {
  const [step, setStep] = useState(() => (prefersReducedMotion() ? count - 1 : 0))
  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const timer = setInterval(() => setStep((current) => (current + 1) % count), ms)
    return () => clearInterval(timer)
  }, [count, ms])
  return step
}

/** Repeatable pseudo-random numbers, so decorations stay in the same place on every render. */
const seeded = (seed) => {
  let value = seed
  return () => {
    value = (value * 9301 + 49297) % 233280
    return value / 233280
  }
}

/* ---------------------------------------------------------------- Backdrops */

function Stars() {
  const stars = useMemo(() => {
    const rand = seeded(7)
    return Array.from({ length: 46 }, (_, i) => ({
      id: i,
      left: `${(rand() * 100).toFixed(2)}%`,
      top: `${(rand() * 100).toFixed(2)}%`,
      size: 2 + Math.round(rand() * 3),
      delay: `${(rand() * 4).toFixed(2)}s`,
      duration: `${(2.5 + rand() * 3).toFixed(2)}s`,
    }))
  }, [])
  return (
    <>
      {stars.map((star) => (
        <span
          key={star.id}
          className="sc-star"
          style={{ left: star.left, top: star.top, width: star.size, height: star.size, animationDelay: star.delay, animationDuration: star.duration }}
        />
      ))}
      <span className="sc-sat">
        <i className="fa-solid fa-satellite" />
      </span>
    </>
  )
}

function Radar() {
  return (
    <>
      <span className="sc-grid" />
      <span className="sc-radar">
        <span className="sc-radar__sweep" />
        <span className="sc-radar__blip sc-radar__blip--a" />
        <span className="sc-radar__blip sc-radar__blip--b" />
      </span>
    </>
  )
}

const NODES = [
  [80, 120], [260, 60], [420, 180], [620, 90], [760, 240], [980, 120], [1180, 200], [1360, 90],
  [200, 380], [520, 420], [860, 460], [1120, 400], [1320, 520], [360, 600], [720, 640], [1040, 640],
]
const LINKS = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [0, 8], [8, 9], [2, 9], [9, 10], [4, 10],
  [10, 11], [6, 11], [11, 12], [7, 12], [8, 13], [13, 14], [9, 14], [14, 15], [10, 15], [15, 12],
]

function NetworkMap() {
  return (
    <svg className="sc-net" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice">
      {LINKS.map(([a, b], i) => (
        <g key={`${a}-${b}`}>
          <line x1={NODES[a][0]} y1={NODES[a][1]} x2={NODES[b][0]} y2={NODES[b][1]} className="sc-net__line" />
          <line
            x1={NODES[a][0]}
            y1={NODES[a][1]}
            x2={NODES[b][0]}
            y2={NODES[b][1]}
            className="sc-net__flow"
            style={{ animationDelay: `${(i % 7) * -0.6}s`, animationDuration: `${3 + (i % 4)}s` }}
          />
        </g>
      ))}
      {NODES.map(([x, y], i) => (
        <g key={`${x}-${y}`}>
          <circle cx={x} cy={y} r="14" className="sc-net__halo" style={{ animationDelay: `${(i % 5) * 0.5}s` }} />
          <circle cx={x} cy={y} r="5" className="sc-net__node" />
        </g>
      ))}
    </svg>
  )
}

function Broadcast() {
  return (
    <>
      <span className="sc-rings sc-rings--a">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="sc-ring" style={{ animationDelay: `${i * 1.25}s` }} />
        ))}
      </span>
      <span className="sc-rings sc-rings--b">
        {[0, 1, 2].map((i) => (
          <span key={i} className="sc-ring" style={{ animationDelay: `${i * 1.6 + 0.6}s` }} />
        ))}
      </span>
    </>
  )
}

const GLYPHS = ['</>', '{ }', '=>', '( )', ';', '[ ]', '#', '&&', 'fn', '01', '</>', '{ }', '=>', '++']

function CodeGlyphs() {
  const items = useMemo(() => {
    const rand = seeded(21)
    return GLYPHS.map((glyph, i) => ({
      id: i,
      glyph,
      left: `${(4 + rand() * 92).toFixed(1)}%`,
      size: 16 + Math.round(rand() * 22),
      delay: `${(-rand() * 18).toFixed(1)}s`,
      duration: `${(14 + rand() * 10).toFixed(1)}s`,
    }))
  }, [])
  return items.map((item) => (
    <span
      key={item.id}
      className="sc-glyph"
      style={{ left: item.left, fontSize: item.size, animationDelay: item.delay, animationDuration: item.duration }}
    >
      {item.glyph}
    </span>
  ))
}

const LEARNING_ICONS = ['fa-graduation-cap', 'fa-book', 'fa-lightbulb', 'fa-award', 'fa-pen-nib', 'fa-star', 'fa-laptop-code', 'fa-certificate']

function LearningIcons() {
  const items = useMemo(() => {
    const rand = seeded(5)
    return Array.from({ length: 14 }, (_, i) => ({
      id: i,
      icon: LEARNING_ICONS[i % LEARNING_ICONS.length],
      left: `${(3 + rand() * 94).toFixed(1)}%`,
      size: 16 + Math.round(rand() * 18),
      delay: `${(-rand() * 16).toFixed(1)}s`,
      duration: `${(12 + rand() * 9).toFixed(1)}s`,
    }))
  }, [])
  return items.map((item) => (
    <i
      key={item.id}
      className={`fa-solid ${item.icon} sc-rise`}
      style={{ left: item.left, fontSize: item.size, animationDelay: item.delay, animationDuration: item.duration }}
    />
  ))
}

const BACKDROPS = {
  starlink: Stars,
  'digital-security': Radar,
  'network-infrastructure': NetworkMap,
  'canal-dstv': Broadcast,
  'software-solutions': CodeGlyphs,
  'training-center': LearningIcons,
}

export function SceneBackdrop({ type, soft = false }) {
  const Backdrop = BACKDROPS[type]
  if (!Backdrop) return null
  return (
    <span className={`sc-backdrop sc-backdrop--${type}${soft ? ' is-soft' : ''}`} aria-hidden="true">
      <Backdrop />
    </span>
  )
}

/* ---------------------------------------------------------------- Photo overlays */

function useClock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const timer = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])
  return now.toLocaleTimeString('en-GB')
}

function CameraView() {
  const time = useClock()
  return (
    <>
      <span className="sc-corner sc-corner--tl" />
      <span className="sc-corner sc-corner--tr" />
      <span className="sc-corner sc-corner--bl" />
      <span className="sc-corner sc-corner--br" />
      <span className="sc-scanline" />
      <span className="sc-motionbox">
        <small>Motion</small>
      </span>
      <span className="sc-rec">
        <span className="sc-rec__dot" />
        REC · CAM 01 · {time}
      </span>
    </>
  )
}

function DishSignal() {
  return (
    <>
      <span className="sc-waves">
        {[0, 1, 2].map((i) => (
          <span key={i} className="sc-wave" style={{ animationDelay: `${i * 0.7}s` }} />
        ))}
      </span>
      <span className="fx-orbit sc-orbit">
        <span className="fx-orbit__dot">
          <i className="fa-solid fa-satellite" />
        </span>
      </span>
    </>
  )
}

function RackLights() {
  const lights = useMemo(() => {
    const rand = seeded(11)
    return Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: `${(52 + rand() * 42).toFixed(1)}%`,
      top: `${(18 + rand() * 70).toFixed(1)}%`,
      tone: i % 3 === 0 ? 'blue' : 'green',
      delay: `${(rand() * 2).toFixed(2)}s`,
      duration: `${(0.6 + rand() * 1.4).toFixed(2)}s`,
    }))
  }, [])
  return lights.map((light) => (
    <span
      key={light.id}
      className={`sc-led is-${light.tone}`}
      style={{ left: light.left, top: light.top, animationDelay: light.delay, animationDuration: light.duration }}
    />
  ))
}

function LiveLabel() {
  return (
    <span className="sc-live">
      <span className="sc-rec__dot" />
      LIVE
    </span>
  )
}

const OVERLAYS = {
  starlink: DishSignal,
  'digital-security': CameraView,
  'network-infrastructure': RackLights,
  'canal-dstv': LiveLabel,
}

export function SceneOverlay({ type }) {
  const Overlay = OVERLAYS[type]
  if (!Overlay) return null
  return (
    <span className={`sc-overlay sc-overlay--${type}`} aria-hidden="true">
      <Overlay />
    </span>
  )
}

/* ---------------------------------------------------------------- Floating widgets */

const ACTIVATION = [
  { icon: 'fa-compass', text: 'Aligning dish…' },
  { icon: 'fa-satellite', text: 'Finding satellites…' },
  { icon: 'fa-signal', text: 'Activating service…' },
  { icon: 'fa-circle-check', text: 'Online', done: true },
]

function StarlinkWidget() {
  const step = useCycle(ACTIVATION.length, 1600)
  const current = ACTIVATION[step]
  return (
    <div className={`sc-card sc-card--activation${current.done ? ' is-done' : ''}`}>
      <span className="sc-card__icon">
        <i className={`fa-solid ${current.icon}`} />
      </span>
      <span>
        <small>Starlink setup</small>
        <strong key={step} className="sc-swap">
          {current.text}
        </strong>
      </span>
      <span className="sc-steps">
        {ACTIVATION.map((item, i) => (
          <span key={item.text} className={i <= step ? 'is-on' : undefined} />
        ))}
      </span>
    </div>
  )
}

function SecurityWidget() {
  const step = useCycle(2, 3200)
  return (
    <div className={`sc-card sc-card--alert${step === 1 ? ' is-alert' : ''}`}>
      <span className="sc-card__icon">
        <i className={`fa-solid ${step === 1 ? 'fa-person-walking' : 'fa-shield-halved'}`} />
      </span>
      <span>
        <small>{step === 1 ? 'Alert · Front gate' : 'All cameras'}</small>
        <strong key={step} className="sc-swap">
          {step === 1 ? 'Motion detected' : 'Recording · secure'}
        </strong>
      </span>
    </div>
  )
}

function NetworkWidget() {
  return (
    <div className="sc-card sc-card--status">
      <span className="sc-card__icon">
        <i className="fa-solid fa-network-wired" />
      </span>
      <span>
        <small>Network status</small>
        <strong>
          <span className="fx-pulse-dot" /> All systems online
        </strong>
      </span>
      <svg className="sc-spark" viewBox="0 0 120 36" aria-hidden="true">
        <polyline points="0,28 12,22 24,26 36,14 48,18 60,8 72,16 84,10 96,20 108,6 120,12" />
      </svg>
    </div>
  )
}

function TvWidget() {
  const channel = useCycle(6, 2200)
  return (
    <div className="sc-card sc-card--tv">
      <span className="sc-eq" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </span>
      <span>
        <small>Now showing</small>
        <strong key={channel} className="sc-swap">
          Channel {101 + channel}
        </strong>
      </span>
      <span className="sc-signal" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
      </span>
    </div>
  )
}

const CODE_LINES = ["const shop = build('your idea')", 'shop.connect(mobileMoney)', 'await shop.launch()']

function CodeWidget() {
  const [typed, setTyped] = useState(() => (prefersReducedMotion() ? CODE_LINES.join('\n').length : 0))
  const total = CODE_LINES.join('\n').length
  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const timer = setInterval(() => setTyped((current) => (current >= total + 30 ? 0 : current + 1)), 70)
    return () => clearInterval(timer)
  }, [total])
  const text = CODE_LINES.join('\n').slice(0, typed)
  const done = typed >= total
  return (
    <div className="sc-card sc-card--code">
      <span className="sc-code__bar">
        <i />
        <i />
        <i />
        <small>app.js</small>
      </span>
      <pre className="sc-code">
        {text}
        {!done && <span className="sc-cursor" />}
      </pre>
      <span className={`sc-deployed${done ? ' is-on' : ''}`}>
        <i className="fa-solid fa-circle-check" /> Deployed
      </span>
    </div>
  )
}

function TrainingWidget() {
  const [percent, setPercent] = useState(() => (prefersReducedMotion() ? 100 : 0))
  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const timer = setInterval(() => setPercent((current) => (current >= 130 ? 0 : current + 2)), 60)
    return () => clearInterval(timer)
  }, [])
  const shown = Math.min(100, percent)
  const done = shown >= 100
  return (
    <div className={`sc-card sc-card--course${done ? ' is-done' : ''}`}>
      <span className="sc-card__icon">
        <i className={`fa-solid ${done ? 'fa-award' : 'fa-book-open'}`} />
      </span>
      <span className="sc-course">
        <small>{done ? 'Well done!' : 'Course progress'}</small>
        <strong>{done ? 'Certificate earned' : `${shown}% complete`}</strong>
        <span className="sc-progress">
          <span style={{ width: `${shown}%` }} />
        </span>
      </span>
    </div>
  )
}

const WIDGETS = {
  starlink: StarlinkWidget,
  'digital-security': SecurityWidget,
  'network-infrastructure': NetworkWidget,
  'canal-dstv': TvWidget,
  'software-solutions': CodeWidget,
  'training-center': TrainingWidget,
}

export function SceneWidget({ type }) {
  const Widget = WIDGETS[type]
  if (!Widget) return null
  return (
    <div className="sc-widget fx-float fx-float--slow" aria-hidden="true">
      <Widget />
    </div>
  )
}
